import { TIKTOK_CONNECT_ERRORS, type TikTokConnectError } from '@sbc/domain-models/gateway';
import { Link, useSearch } from '@tanstack/react-router';
import { Music2, Plus } from 'lucide-react';
import { useState } from 'react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useChannelConnections, useConnectTikTok } from '@/lib/api/channel-connections';

const PAGE_SIZE = 20;

const TIKTOK_ERROR_COPY: Record<TikTokConnectError, string> = {
  unavailable:
    'TikTok is not set up on the platform yet (the TikTok app id and secret are missing).',
  denied: 'TikTok did not grant access. Try again and approve every permission.',
  failed: 'Connecting TikTok failed. Try again; if it keeps failing, check the server logs.',
};

export interface ConnectionsSearch {
  tiktokError?: TikTokConnectError;
}

export function parseConnectionsSearch(search: Record<string, unknown>): ConnectionsSearch {
  const value = search.tiktokError;
  const tiktokError = TIKTOK_CONNECT_ERRORS.find((code) => code === value);
  return tiktokError ? { tiktokError } : {};
}

export function ConnectionsListPage() {
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const { tiktokError } = useSearch({ strict: false });
  const connectTikTok = useConnectTikTok();

  const connectionsQuery = useChannelConnections({
    page,
    pageSize: PAGE_SIZE,
    search: search.trim() || undefined,
  });

  const onSearch = (value: string) => {
    setSearch(value);
    setPage(1);
  };

  const result = connectionsQuery.data;
  const pageCount = result ? Math.max(1, Math.ceil(result.total / result.pageSize)) : 1;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Channels</h1>
          <p className="text-sm text-[var(--color-muted-foreground)]">
            External accounts the gateway talks to, and their credentials.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            disabled={connectTikTok.isPending}
            onClick={() => connectTikTok.mutate()}
          >
            <Music2 className="size-4" />
            Connect TikTok account
          </Button>
          <Button asChild>
            <Link to="/gateway/connections/new">
              <Plus className="size-4" />
              New connection
            </Link>
          </Button>
        </div>
      </div>

      {tiktokError && (
        <p className="text-sm text-[var(--color-destructive)]">{TIKTOK_ERROR_COPY[tiktokError]}</p>
      )}
      {connectTikTok.isError && (
        <p className="text-sm text-[var(--color-destructive)]">
          Could not start the TikTok connection: {connectTikTok.error.message}
        </p>
      )}

      <Input
        placeholder="Search by handle or account id…"
        value={search}
        onChange={(event) => onSearch(event.target.value)}
        className="h-8 w-full sm:w-72"
      />

      {connectionsQuery.isPending && <p className="text-sm">Loading…</p>}
      {connectionsQuery.isError && (
        <p className="text-sm text-[var(--color-destructive)]">
          Failed to load connections: {connectionsQuery.error.message}
        </p>
      )}

      {result && (
        <>
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Handle</TableHead>
                  <TableHead>Channel</TableHead>
                  <TableHead>Account id</TableHead>
                  <TableHead>Bindings</TableHead>
                  <TableHead>Credentials</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {result.items.length ? (
                  result.items.map((connection) => {
                    const configured =
                      connection.verifyTokenSet &&
                      connection.appSecretSet &&
                      connection.accessTokenSet;
                    return (
                      <TableRow key={connection.id}>
                        <TableCell>
                          <Link
                            to="/gateway/connections/$connectionId"
                            params={{ connectionId: connection.id }}
                            className="font-medium hover:underline"
                          >
                            {connection.handle}
                          </Link>
                        </TableCell>
                        <TableCell>
                          <Badge variant="secondary">{connection.channel}</Badge>
                        </TableCell>
                        <TableCell className="text-sm">{connection.externalAccountId}</TableCell>
                        <TableCell className="text-sm">{connection.bindingCount}</TableCell>
                        <TableCell>
                          {configured ? (
                            <Badge variant="secondary">Configured</Badge>
                          ) : (
                            <Badge variant="outline">Incomplete</Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          {connection.enabled ? (
                            <Badge variant="secondary">Enabled</Badge>
                          ) : (
                            <Badge variant="outline">Disabled</Badge>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })
                ) : (
                  <TableRow>
                    <TableCell colSpan={6} className="h-24 text-center">
                      No connections yet.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>

          <div className="flex items-center justify-between gap-4">
            <p className="text-sm text-[var(--color-muted-foreground)]">
              {result.total} connection(s)
            </p>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage((value) => Math.max(1, value - 1))}
                disabled={page <= 1}
              >
                Previous
              </Button>
              <span className="text-sm text-[var(--color-muted-foreground)]">
                Page {page} of {pageCount}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage((value) => value + 1)}
                disabled={page >= pageCount}
              >
                Next
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
